import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, FileClock, Truck, PackageCheck, Store, Users, Package, Wallet } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { api, errMsg, fmt } from "../../lib/api";
import { ScopeSelect, ScopeNote } from "../../components/ScopeSelect";
import { PageTitle } from "../../components/ui";
import AdminOrders from "./AdminOrders";
import { AdminStores } from "./AdminStoresBank";
import { AdminReports } from "./AdminOps";
import { AdminTopups } from "./AdminTopupsOboWay";
import AdminTransfer from "./AdminTransfer";
import { useCategories } from "../../lib/categories";
import { ProductEditDialog } from "./AdminProductEdit";
import AdminCatalog from "./AdminCatalog";
import AdminReviews from "./AdminReviews";
import AdminReturns from "./AdminReturns";
import AdminSite from "./AdminSite";
import AdminObogo from "./AdminObogo";
import AdminRideDemo from "./AdminRideDemo";
import AdminSettings from "./AdminSettings";
import AdminData from "./AdminData";
import AdminComplaints from "./AdminComplaints";
import { Field, btnPrimary, inputCls } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import AdminUsers from "./AdminUsers";
import AdminAudit from "./AdminAudit";
import AdminFinance from "./AdminFinance";
import { MfaSetup, SecurityTab } from "./AdminSecurity";

const ROLE_LABEL = { admin: "Super Admin", ops_admin: "Administrador operacional", store_manager: "Gestor de lojas",
  obogo_manager: "Gestor OBÔGO", finance_manager: "Gestor financeiro", support: "Suporte" };

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bo-card flex items-center gap-3 p-4" data-testid={testid}>
    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-bo-green text-bo-yellow"><Icon className="h-5 w-5" /></span>
    <div><p className="text-xl font-extrabold">{value}</p><p className="text-xs text-gray-500">{label}</p></div>
  </div>
);

function Overview() {
  const [s, setS] = useState(null);
  const [scope, setScope] = useState("real");
  useEffect(() => { api.get("/admin/stats", { params: { scope } }).then(({ data }) => setS(data)); }, [scope]);
  if (!s) return null;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3"><ScopeSelect value={scope} onChange={setScope} testid="overview-scope-select" /><ScopeNote scope={scope} /></div>
      {!s.bank_accounts_ready && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Configure uma conta bancária ativa e verificada para ativar pagamentos por transferência.</p>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={ClipboardList} label="Encomendas" value={s.orders} testid="admin-stat-orders" />
        <Stat icon={FileClock} label="Comprovativos por validar" value={s.awaiting_validation} testid="admin-stat-validation" />
        <Stat icon={Truck} label="Prontas para entrega" value={s.to_dispatch} testid="admin-stat-dispatch" />
        <Stat icon={PackageCheck} label="Entregues" value={s.delivered} testid="admin-stat-delivered" />
        <Stat icon={Store} label="Lojas pendentes" value={s.pending_stores} testid="admin-stat-stores" />
        <Stat icon={Users} label="Clientes" value={s.customers} testid="admin-stat-customers" />
        <Stat icon={Package} label="Produtos ativos" value={s.products} testid="admin-stat-products" />
        <Stat icon={Wallet} label="Receita confirmada" value={fmt(s.confirmed_revenue)} testid="admin-stat-revenue" />
      </div>
    </div>
  );
}

function Products() {
  const CATEGORIES = useCategories();
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/products").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const [f, setF] = useState({ name: "", brand: "", category: CATEGORIES[0].key, unit: "" });
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/products", f); toast.success("Produto real criado (sem avaliações nem descontos)"); setF({ ...f, name: "", brand: "", unit: "" }); load(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const demo = async (p) => {
    try { await api.patch(`/admin/products/${p.id}/demo`, { is_demo: !p.is_demo }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (p) => {
    try { await api.patch(`/admin/products/${p.id}`, null, { params: { active: !p.active } }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3">
    <form onSubmit={create} className="bo-card grid gap-3 p-4 sm:grid-cols-5" data-testid="admin-product-form">
      <Field label="Nome"><input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="admin-product-name" /></Field>
      <Field label="Marca"><input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className={inputCls} data-testid="admin-product-brand" /></Field>
      <Field label="Categoria"><select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls} data-testid="admin-product-category">{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}</select></Field>
      <Field label="Unidade"><input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className={inputCls} data-testid="admin-product-unit" /></Field>
      <div className="flex items-end"><button className={btnPrimary} data-testid="admin-product-create">Criar produto real</button></div>
    </form>
    <div className="bo-card divide-y divide-gray-50" data-testid="admin-products">
      {items.map((p) => (
        <div key={p.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`admin-product-${p.id}`}>
          <img src={p.image} alt="" className="h-9 w-9 rounded-lg object-cover" />
          <span className="flex-1 font-medium">{p.name}</span>
          <button onClick={() => demo(p)} className={`rounded-full px-3 py-1 text-xs font-semibold ${p.is_demo ? "bg-gray-900 text-white" : "bg-bo-yellow text-bo-ink"}`} data-testid={`admin-product-demo-${p.id}`}>{p.is_demo ? "DEMO" : "Verificado"}</button>
          <span className="text-xs text-gray-500">{p.category_name}</span>
          <button onClick={() => setEditing(p)} className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold hover:bg-gray-50" data-testid={`admin-product-edit-${p.id}`}>Editar</button>
          <button onClick={() => toggle(p)} className={`rounded-full px-3 py-1 text-xs font-semibold ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-200"}`} data-testid={`admin-product-toggle-${p.id}`}>{p.active ? "Ativo" : "Inativo"}</button>
        </div>
      ))}
    </div>
    {editing && <ProductEditDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const TABS = [
  ["overview", "Resumo", "dashboard.view", Overview], ["orders", "Encomendas", "orders.view", AdminOrders],
  ["deliveries", "OBÔGO Entregas", "obogo.manage", AdminObogo], ["ride", "OBÔGO Ride (DEMO)", "obogo.manage", AdminRideDemo], ["topups", "OboWay · Recargas", "topups.view", AdminTopups],
  ["reports", "Relatórios", "reports.view", AdminReports], ["stores", "Lojas", "stores.manage", AdminStores],
  ["finance", "Finanças", "finance.view", AdminFinance], ["bank", "Pagamentos por Transferência", "finance.bank", AdminTransfer], ["products", "Produtos", "products.manage", Products],
  ["catalog", "Categorias e marcas", "products.manage", AdminCatalog], ["reviews", "Avaliações", "reviews.moderate", AdminReviews],
  ["returns", "Devoluções", "returns.manage", AdminReturns], ["site", "Gestão do site", "content.manage", AdminSite],
  ["complaints", "Reclamações", "complaints.manage", AdminComplaints], ["settings", "Configurações", "settings.manage", AdminSettings], ["data", "Dados reais / teste", "settings.manage", AdminData],
  ["users", "Utilizadores e acessos", "users.view", AdminUsers], ["audit", "Auditoria", "audit.view", AdminAudit],
  ["security", "Segurança", null, SecurityTab],
];

export default function AdminPanel() {
  const { user, reload } = useAuth();
  const [tab, setTab] = useState(null);
  if (user.role === "admin" && user.mfa_enabled && !user.session_mfa) {
    return (
      <div data-testid="admin-panel">
        <PageTitle title="Administração" sub="Proteja a conta de Super Admin antes de continuar." />
        {user.mfa_enabled
          ? <p className="bo-card p-5 text-sm" data-testid="admin-mfa-relogin">Termine a sessão e entre novamente com o código da aplicação autenticadora.</p>
          : <MfaSetup onDone={reload} />}
      </div>
    );
  }
  const tabs = TABS.filter(([, , perm]) => !perm || user.permissions?.includes(perm));
  const showMfaTip = user.role === "admin" && !user.mfa_enabled;
  return (
    <div data-testid="admin-panel">
      <PageTitle title="Administração" sub={`Sessão de ${user.name} · perfil ${ROLE_LABEL[user.role] || user.role}.`} />
      {showMfaTip && <p className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="admin-mfa-tip">
        Recomendado: ative a verificação em dois passos (aplicação autenticadora + códigos de recuperação).
        <button onClick={() => setTab("security")} className="font-semibold underline" data-testid="admin-mfa-tip-btn">Ativar em Segurança</button></p>}
      <Tabs value={tab || tabs[0][0]} onValueChange={setTab}>
        <TabsList className="mb-4 h-auto flex-wrap">{tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
        {tabs.map(([v, , , C]) => <TabsContent key={v} value={v}><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
